import DetailsListItem from "components/DetailsList/components/DetailsListItem";
import type { FunctionComponent } from "react";
import { Fragment, useMemo } from "react";
import { generatePath, NavLink } from "react-router-dom";

import Card from "~/components/Card";
import CardHeader from "~/components/Card/components/CardHeader";
import CardTitle from "~/components/Card/components/CardTitle";
import DetailsListDivider from "~/components/DetailsList/components/DetailsListDivider";
import { CLIENT_DETAILS_PATH, PROJECT_DETAILS_PATH_WITH_TAB_ID } from "~/constants/appRoutes.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { ProjectDetailsTabName } from "~/modules/project/types/projectViewTypes.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

import InvoiceDetailsLink from "../InvoiceDetailsLink";

type Props = {
	invoiceData: Invoice,
	onUpdateClick?: () => void;
};

const InvoiceDetailsSection: FunctionComponent<Props> = ({ onUpdateClick, invoiceData }) => {
	const title = invoiceData.isProjectInvoice ? "Kunden- / Projektinformationen" : "Beschreibung";

	const PhasesAndOrdersSectionOneOrder = useMemo(() => {
		if (invoiceData.orders?.length === 1) {
			const phaseData = invoiceData.projectPhases[0];
			const orderData = invoiceData.orders[0];
			return (<>
					<DetailsListDivider />
					<DetailsListItem label="Phase">
						{phaseData ?
							<InvoiceDetailsLink
								to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
									projectId: invoiceData.projectId,
									tabId: `phase-${phaseData.phaseNumber}`,
								})}>{`${phaseData.phaseNumber} - ${phaseData.title}`}
							</InvoiceDetailsLink>
							: EMPTY_VALUE_PLACEHOLDER}
					</DetailsListItem>
					<DetailsListDivider />
					<DetailsListItem label="Bestellung">
						{orderData ?
							<NavLink
								className="flex flex-col transition-colors duration-200 hover:text-secondary-500"
								to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
									projectId: invoiceData.projectId,
									tabId: `phase-${phaseData.phaseNumber}`,
								})}>{orderData.title}
							</NavLink>
							: EMPTY_VALUE_PLACEHOLDER}
					</DetailsListItem>
				</>
			);
		}
		return null;
	}, [invoiceData]);

	const PhasesAndOrdersSectionMultipleOrders = useMemo(() => {
		if (invoiceData.orders?.length > 1) {
			const phasesSorted = invoiceData.projectPhases.sort(byObjectProperty("phaseNumber"));
			const ordersSorted = invoiceData.orders.sort(byObjectProperty("createdAt"));

			return (<>
					<CardHeader hasTitle={true}>
						<CardTitle>Enthaltene Bestellungen</CardTitle>
					</CardHeader>
					<div className="grid grid-cols-2 gap-x-4 p-4 text-sm">
						<div className="font-medium leading-6 text-gray-900">
							Phase
						</div>
						<div className="font-medium leading-6 text-gray-900">
							Bestellung
						</div>
						<div className="col-span-2" />
						{phasesSorted.map(phase => {
							return <Fragment key={phase.id}>
								<DetailsListDivider />
								<InvoiceDetailsLink
									className="pt-2"
									to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
										projectId: invoiceData.projectId,
										tabId: `phase-${phase.phaseNumber}`,
									})}>{`${phase.phaseNumber} - ${phase.title}`}
								</InvoiceDetailsLink>
								<div className="flex flex-col gap-y-2 py-2">
									{ordersSorted.filter(order => order.projectPhaseId === phase.id).map(order =>
										<NavLink
											key={order.id}
											className="flex flex-col transition-colors duration-200 hover:text-secondary-500"
											to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
												projectId: invoiceData.projectId,
												tabId: `phase-${phase.phaseNumber}`,
											})}>{order.title}
										</NavLink>,
									)}
								</div>
							</Fragment>;
						})}
					</div>
				</>
			);
		}
		return null;
	}, [invoiceData]);

	return <Card title={title}
				 padding="none"
				 onHeaderButtonClick={onUpdateClick}
				 headerButtonText={!invoiceData.projectId ? "Projekt und Bestellung zuweisen" : "Bestellung zuweisen"}
	>
		<div className="grid grid-cols-[auto_1fr] gap-4 p-4">
			{invoiceData.isProjectInvoice ? <>
				<DetailsListItem label="Kunde">
					{invoiceData.clientId ? <InvoiceDetailsLink
						to={generatePath(CLIENT_DETAILS_PATH, { clientId: invoiceData.clientId })}
					>
						{invoiceData.clientName}
					</InvoiceDetailsLink> : EMPTY_VALUE_PLACEHOLDER}
				</DetailsListItem>
				<DetailsListDivider />
				<DetailsListItem label="Projekt">
					{invoiceData.projectTitle ?
						<InvoiceDetailsLink
							to={generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
								projectId: invoiceData.projectId,
								tabId: ProjectDetailsTabName.Invoices,
							})}>{invoiceData.projectTitle}
						</InvoiceDetailsLink>
						: EMPTY_VALUE_PLACEHOLDER}
				</DetailsListItem>
				{PhasesAndOrdersSectionOneOrder}
			</> : <>{invoiceData.description}</>}
		</div>
		{PhasesAndOrdersSectionMultipleOrders}
	</Card>;
};

export default InvoiceDetailsSection;